/**
 * Role and Permission Constants
 * Matches the backend enum definitions from the API
 */

/**
 * User Roles
 * Matches UserRole enum from backend:
 * - SuperAdmin = 0 (Full system access across all organizations)
 * - OrganizationAdmin = 1 (Manage users and permissions within their organization)
 * - User = 2 (Regular user with granular permissions per form)
 */
export const UserRole = {
  SUPER_ADMIN: 0,
  ORGANIZATION_ADMIN: 1, // Also accessible as ADMIN for backward compatibility
  ADMIN: 1, // Alias for ORGANIZATION_ADMIN
  USER: 2,
};

/**
 * User Role Display Names
 */
export const UserRoleNames = {
  [UserRole.SUPER_ADMIN]: 'Super Admin',
  [UserRole.ORGANIZATION_ADMIN]: 'Organization Admin',
  [UserRole.USER]: 'User',
};

/**
 * Permission Flags (Bitwise)
 * Matches Permission enum from backend
 */
export const Permission = {
  NONE: 0,
  READ: 1,
  CREATE: 2,
  UPDATE: 4,
  DELETE: 8,
  ALL_BASIC: 15, // READ + CREATE + UPDATE + DELETE
  MANAGE_PERMISSIONS: 16,
  MANAGE_USERS: 32,
  MANAGE_ORGANIZATION: 64,
  SUPER_ADMIN_ONLY: 128,
  ALL_MANAGEMENT: 240, // MANAGE_PERMISSIONS + MANAGE_USERS + MANAGE_ORGANIZATION + SUPER_ADMIN_ONLY
  ALL: 255, // All permissions
};

/**
 * Permission Display Names
 */
export const PermissionNames = {
  [Permission.NONE]: 'None',
  [Permission.READ]: 'Read',
  [Permission.CREATE]: 'Create',
  [Permission.UPDATE]: 'Update',
  [Permission.DELETE]: 'Delete',
  [Permission.ALL_BASIC]: 'All Basic',
  [Permission.MANAGE_PERMISSIONS]: 'Manage Permissions',
  [Permission.MANAGE_USERS]: 'Manage Users',
  [Permission.MANAGE_ORGANIZATION]: 'Manage Organization',
  [Permission.SUPER_ADMIN_ONLY]: 'Super Admin Only',
  [Permission.ALL_MANAGEMENT]: 'All Management',
  [Permission.ALL]: 'All Permissions',
};

/**
 * Check if user has a specific role
 * @param {number} userRole - User's role
 * @param {number} requiredRole - Required role
 * @returns {boolean} True if user has required role or higher
 */
export const hasRole = (userRole, requiredRole) => {
  return userRole <= requiredRole; // Lower number = higher privilege
};

/**
 * Check if user is Super Admin
 * @param {number} userRole - User's role
 * @returns {boolean} True if user is super admin
 */
export const isSuperAdmin = (userRole) => {
  return userRole === UserRole.SUPER_ADMIN;
};

/**
 * Check if user is Organization Admin or higher
 * @param {number} userRole - User's role
 * @returns {boolean} True if user is organization admin or super admin
 */
export const isAdmin = (userRole) => {
  return userRole <= UserRole.ORGANIZATION_ADMIN;
};

/**
 * Check if user has a specific permission (bitwise check)
 * @param {number} userPermissions - User's permissions (bitwise)
 * @param {number} requiredPermission - Required permission flag
 * @returns {boolean} True if user has the permission
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  return (userPermissions & requiredPermission) === requiredPermission;
};

/**
 * Get list of all basic permissions for UI
 */
export const getBasicPermissions = () => [
  { value: Permission.READ, label: PermissionNames[Permission.READ] },
  { value: Permission.CREATE, label: PermissionNames[Permission.CREATE] },
  { value: Permission.UPDATE, label: PermissionNames[Permission.UPDATE] },
  { value: Permission.DELETE, label: PermissionNames[Permission.DELETE] },
];

/**
 * Get list of all management permissions for UI
 */
export const getManagementPermissions = () => [
  { value: Permission.MANAGE_PERMISSIONS, label: PermissionNames[Permission.MANAGE_PERMISSIONS] },
  { value: Permission.MANAGE_USERS, label: PermissionNames[Permission.MANAGE_USERS] },
  { value: Permission.MANAGE_ORGANIZATION, label: PermissionNames[Permission.MANAGE_ORGANIZATION] },
];

/**
 * Get all roles for UI dropdowns
 */
export const getAllRoles = () => [
  { value: UserRole.SUPER_ADMIN, label: UserRoleNames[UserRole.SUPER_ADMIN] },
  { value: UserRole.ORGANIZATION_ADMIN, label: UserRoleNames[UserRole.ORGANIZATION_ADMIN] },
  { value: UserRole.USER, label: UserRoleNames[UserRole.USER] },
];

/**
 * Get available roles based on current user's role
 * Super Admins can assign all roles
 * Organization Admins can only assign Organization Admin and User roles
 * @param {number} currentUserRole - Current user's role
 * @returns {Array} Array of available role objects
 */
export const getAvailableRoles = (currentUserRole) => {
  const allRoles = getAllRoles();

  // Super Admin can assign any role
  if (isSuperAdmin(currentUserRole)) {
    return allRoles;
  }

  // Organization Admin cannot assign Super Admin role
  if (isAdmin(currentUserRole)) {
    return allRoles.filter(role => role.value !== UserRole.SUPER_ADMIN);
  }

  // Fallback: regular users shouldn't assign roles, but safeguard with user-only
  return allRoles.filter(role => role.value === UserRole.USER);
};

/**
 * Get role display name
 * @param {number|string} role - User role value (number or string)
 * @returns {string} Role display name
 */
export const getRoleName = (role) => {
  // If it's already a number, look it up directly
  if (typeof role === 'number') {
    return UserRoleNames[role] || 'Unknown';
  }

  // If it's a string, convert to number first
  if (typeof role === 'string') {
    const roleUpper = role.toUpperCase();

    // SuperAdmin variations
    if (roleUpper === 'SUPERADMIN' || roleUpper === 'SUPER_ADMIN' || roleUpper === 'SYSTEMADMIN' || roleUpper === 'SYSTEM_ADMIN') {
      return UserRoleNames[UserRole.SUPER_ADMIN];
    }

    // OrganizationAdmin/Admin variations
    if (roleUpper === 'ADMIN' || roleUpper === 'ORGANIZATIONADMIN' || roleUpper === 'ORGANIZATION_ADMIN') {
      return UserRoleNames[UserRole.ORGANIZATION_ADMIN];
    }

    // User variations
    if (roleUpper === 'USER' || roleUpper === 'NORMALUSER' || roleUpper === 'NORMAL_USER') {
      return UserRoleNames[UserRole.USER];
    }
  }

  return 'Unknown';
};

/**
 * Get permission display name
 * @param {number} permission - Permission value
 * @returns {string} Permission display name
 */
export const getPermissionName = (permission) => {
  return PermissionNames[permission] || 'Unknown';
};

/**
 * Combine multiple permissions into a single bitwise value
 * @param {number[]} permissions - Array of permission flags
 * @returns {number} Combined permission value
 */
export const combinePermissions = (permissions) => {
  return permissions.reduce((acc, permission) => acc | permission, 0);
};

/**
 * Get array of individual permissions from a bitwise value
 * @param {number} permissionValue - Bitwise permission value
 * @returns {number[]} Array of individual permission flags
 */
export const getIndividualPermissions = (permissionValue) => {
  const permissions = [];
  const allFlags = [
    Permission.READ,
    Permission.CREATE,
    Permission.UPDATE,
    Permission.DELETE,
    Permission.MANAGE_PERMISSIONS,
    Permission.MANAGE_USERS,
    Permission.MANAGE_ORGANIZATION,
    Permission.SUPER_ADMIN_ONLY,
  ];

  allFlags.forEach(flag => {
    if (hasPermission(permissionValue, flag)) {
      permissions.push(flag);
    }
  });

  return permissions;
};
